import { Button, ActionIcon, Menu, Group, ThemeIcon, Tooltip, useMantineColorScheme } from "@mantine/core";
import { Settings, ChevronDown, Play, Info, Edit, Trash } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import {
    selectReportFull,
    selectReportExecuting,
} from "@/store/slices/reportSlice";
import { validateVariables } from "@bi-studio/shared/lib";

export interface ReportActionsButtonProps {
    setIsDetailsOpen: (open: boolean) => void;
    setIsDeleteModalOpen: (open: boolean) => void;
    handleExecute: (values?: Record<string, string>, cache?: boolean, newPage?: number) => void;
}

export function ReportActionsButton({
    setIsDetailsOpen,
    setIsDeleteModalOpen,
    handleExecute
}: ReportActionsButtonProps) {
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();
    const { id: reportId } = useParams<{ id: string }>();

    const {
        requiredVariables,
        variableValues,
        cachingPolicies
    } = useAppSelector(selectReportFull);

    const isExecuting = useAppSelector(selectReportExecuting);

    return (
        <Group gap="xs">
            <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                    <Group gap={0}>
                        <Button
                            visibleFrom="sm"
                            color="black"
                            c="white"
                            leftSection={<Settings size={16} />}
                            rightSection={<ChevronDown size={14} />}
                        >
                            Actions
                        </Button>
                        <Tooltip label="Actions">
                            <ActionIcon
                                hiddenFrom="sm"
                                variant="filled"
                                color="black"
                                c="white"
                                size="lg"
                            >
                                <Settings size={20} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Menu.Target>
                <Menu.Dropdown
                    style={{
                        backgroundColor: colorScheme === 'dark' ? 'var(--modal-bg)' : undefined,
                        borderColor: colorScheme === 'dark' ? 'var(--modal-border)' : undefined,
                        backdropFilter: 'blur(var(--modal-blur))'
                    }}
                >
                    <Menu.Item leftSection={<Play size={16} />} onClick={() => handleExecute(variableValues, false, 1)} disabled={isExecuting || (requiredVariables.length > 0 && !validateVariables(requiredVariables, variableValues).valid)}>
                        {isExecuting ? "Running..." : "Run"}
                    </Menu.Item>
                    {cachingPolicies.enableCaching && cachingPolicies.webUsersCaching && !cachingPolicies.autoCaching && (
                        <Menu.Item leftSection={<ThemeIcon variant="light" size="sm" color="orange"><Play size={10} /></ThemeIcon>} onClick={() => handleExecute(variableValues, true, 1)} disabled={isExecuting}>Cache Report</Menu.Item>
                    )}
                    <Menu.Item leftSection={<Info size={16} />} onClick={() => setIsDetailsOpen(true)}>View Details</Menu.Item>
                    <Menu.Divider />
                    <Menu.Item leftSection={<Edit size={16} />} onClick={() => navigate(`/reports/${reportId}/edit`)}>Edit</Menu.Item>
                    <Menu.Item color="red" leftSection={<Trash size={16} />} onClick={() => setIsDeleteModalOpen(true)}>Delete</Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Group >
    );
}
